import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Season } from "@mysnowscape/snow-lib";

import axiosRoutes from "../../../../../services/axios/routes";
import routeBuilder from "../../../../../services/axios/routeBuilder";
import { useRouteBuilder } from "../context/RouteBuilderContext";
import {
  addUnassignedRouteOptions,
  selectAddUnassignedCatalog,
  type SavedCatalogRoute,
  sortAddUnassignedCatalog,
  withMatchingServicesFirst,
} from "../lib/addUnassignedCatalog";
import {
  recencyToServicedAfter,
  shouldShowRecency,
} from "../lib/serviceHelpers";
import { withDefaultServiceTime } from "../lib/generationProperties";

export function useAddUnassignedCatalog() {
  const {
    session,
    availableServices,
    addUnassignedFilters: filters,
    setAddUnassignedFilters,
  } = useRouteBuilder();
  const { serviceTypes, recency, sort } = filters;
  const routeType = session?.inputs.routeType ?? "recurring";
  const season = session?.inputs.season ?? Season.snow;
  const showRecency = shouldShowRecency(routeType, season);
  // Only recency is a server filter. Freeze its cutoff until the filter changes
  // so renders and drags cannot keep changing the query key.
  const servicedAfter = useMemo(
    () =>
      session?.id && showRecency ? recencyToServicedAfter(recency) : undefined,
    [session?.id, showRecency, recency],
  );
  const companyId = session?.companyId ?? "";
  const query = useQuery({
    queryKey: [
      "route-builder",
      "add-unassigned",
      companyId,
      season,
      servicedAfter ?? null,
    ],
    enabled: Boolean(companyId) && Boolean(session),
    queryFn: ({ signal }) =>
      routeBuilder.loadBuilderProperties({
        companyId,
        season,
        servicedAfter,
        includeAssignedRoutes: true,
        signal,
      }),
  });

  const routesQuery = useQuery<SavedCatalogRoute[]>({
    queryKey: ["routes", companyId],
    enabled: Boolean(companyId),
    queryFn: ({ signal }) =>
      axiosRoutes.getAllRoutesAsync(companyId, signal, undefined),
  });
  const routeOptions = useMemo(
    () =>
      session
        ? addUnassignedRouteOptions(
            Array.isArray(routesQuery.data) ? routesQuery.data : [],
            session,
          )
        : [],
    [routesQuery.data, session],
  );

  const properties = useMemo(() => {
    if (!session) return [];
    // Recency membership comes from the server, while local notes/estimates stay intact.
    const saved = new Map(session.availableProperties.map((p) => [p.jobId, p]));
    const loaded = (query.data ?? []).map((p) => {
      const local = saved.get(p.jobId);
      return withDefaultServiceTime(
        local
          ? {
              ...p,
              ...local,
              updatedAt: p.updatedAt ?? local.updatedAt,
              assignedRoutes: p.assignedRoutes,
            }
          : p,
        session.inputs.defaultServiceTimeMinutes,
      );
    });
    return sortAddUnassignedCatalog(
      withMatchingServicesFirst(
        selectAddUnassignedCatalog(loaded, session, filters),
        serviceTypes,
      ),
      sort,
    );
  }, [query.data, session, filters, serviceTypes, sort]);

  return {
    properties,
    filters,
    setFilters: setAddUnassignedFilters,
    routeOptions,
    showRecency,
    routeType,
    availableServices,
    isLoading: query.isFetching || routesQuery.isFetching,
    isError: query.isError || routesQuery.isError,
  };
}
